import { Loader } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useState, createContext, useEffect } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { useNavigate } from "react-router-dom";

import {
  executedServiceOrdersByServiceOrderID,
  getCostCenter,
  getEquipment,
  getExecutedServiceOrder,
  getMaintenanceRoutine,
  getServiceOrder,
  getSpare,
  getTool,
  listUsers,
  serviceOrderEquipmentsByServiceOrderId,
} from "graphql/queries";
import {
  localDeleteServiceOrder,
  localGetServiceOrder,
  localSaveServiceOrder,
} from "libs/idb/service-orders-db";
import { useOnlineStatus } from "hooks/useOnlineStatus";
import {
  getBranchName,
  getCompanyName,
  getUserEmail,
} from "graphql/custom-queries";
import { serviceOrderStatus } from "config/constants";

export const OrderContext = createContext({});

export const OrderProvider = ({
  serviceOrderID,
  viewType = "execute",
  fromEquipment = false,
  children,
}) => {
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const [serviceOrder, setServiceOrder] = useState();
  const [routines, setRoutines] = useState([]);
  const [localData, setLocalData] = useState(null);

  const saveLocalData = async (values) => {
    try {
      const data = (await localGetServiceOrder(serviceOrder.id)) || {};
      await localSaveServiceOrder(serviceOrder.id, {
        ...data,
        serviceOrder: {
          ...serviceOrder,
          offline: true,
          ...(values.executionStartedAt
            ? { status: serviceOrderStatus.executing }
            : {}),
        },
        execution: {
          ...(data.execution ?? {}),
          ...values,
        },
        sync: false,
      });
      setLocalData({
        execution: {
          ...(data.execution ?? {}),
          ...values,
        },
      });
      if (values.executionStartedAt)
        setServiceOrder((prev) => ({
          ...prev,
          status: serviceOrderStatus.executing,
        }));
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    }
  };

  const saveLocalUsers = async () => {
    try {
      const ids = [
        serviceOrder.userExecutor,
        ...(serviceOrder.auxExecutors || []),
      ];
      let usersItems = [];
      if (ids?.length > 0) {
        const userData = await API.graphql(
          graphqlOperation(listUsers, {
            filter: {
              or: ids?.map((id) => ({ id: { eq: id } })) ?? [],
            },
          })
        );
        usersItems = userData.data.listUsers.items;
      }
      const data = (await localGetServiceOrder(serviceOrder.id)) || {};
      await localSaveServiceOrder(serviceOrder.id, {
        ...data,
        users: usersItems,
      });
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    }
  };

  const saveLocalTools = async () => {
    try {
      const ids = serviceOrder.tools || [];
      let toolsItems = [];
      if (ids?.length > 0) {
        await Promise.all(
          ids.map(async (id) => {
            const toolData = await API.graphql(
              graphqlOperation(getTool, {
                id,
              })
            );
            const toolItem = toolData.data.getTool;
            toolsItems.push(toolItem);
          }) ?? []
        );
      }
      const data = (await localGetServiceOrder(serviceOrder.id)) || {};
      await localSaveServiceOrder(serviceOrder.id, {
        ...data,
        tools: toolsItems,
      });
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    }
  };

  const saveLocalSpares = async () => {
    try {
      const ids = serviceOrder.spares || [];
      let sparesItems = [];
      if (ids?.length > 0) {
        await Promise.all(
          ids.map(async (id) => {
            const spareData = await API.graphql(
              graphqlOperation(getSpare, {
                id,
              })
            );
            const spareItem = spareData.data.getSpare;

            sparesItems.push(spareItem);
          }) ?? []
        );
      }
      const data = (await localGetServiceOrder(serviceOrder.id)) || {};
      await localSaveServiceOrder(serviceOrder.id, {
        ...data,
        spares: sparesItems,
      });
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    }
  };

  const saveLocalEquipments = async () => {
    try {
      const ids =
        serviceOrder.equipmentRoutines?.map((eq) => eq.equipmentID) || [];
      const equipmentItems = [];
      if (ids.length > 0) {
        await Promise.all(
          ids.map(async (id) => {
            const equipmentData = await API.graphql(
              graphqlOperation(getEquipment, { id })
            );
            if (equipmentData.data.getEquipment) {
              equipmentItems.push(equipmentData.data.getEquipment);
            }
          })
        );
      }
      const data = (await localGetServiceOrder(serviceOrder.id)) || {};
      await localSaveServiceOrder(serviceOrder.id, {
        ...data,
        equipments: equipmentItems,
      });
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    }
  };

  const saveLocalRoutines = async () => {
    try {
      const ids = [
        ...new Set([
          ...(serviceOrder.maintenanceRoutines ?? []),
          ...(serviceOrder.equipmentRoutines?.map((eq) => eq.routineID) ?? []),
        ]),
      ]?.filter((id) => !!id) ?? [];
      let routinesList = [];
      let activitiesList = [];

      if (ids?.length > 0) {
        await Promise.all(
          ids.map(async (id) => {
            const routinesData = await API.graphql(
              graphqlOperation(getMaintenanceRoutine, {
                id,
              })
            );
            const routineItem = routinesData.data.getMaintenanceRoutine;

            routinesList.push(routineItem);
            activitiesList.push(
              ...(routineItem.activities?.items?.filter(
                (act) => !act.deletedAt
              ) ?? [])
            );
          }) ?? []
        );
      }
      const data = (await localGetServiceOrder(serviceOrder.id)) || {};
      await localSaveServiceOrder(serviceOrder.id, {
        ...data,
        routines: routinesList,
        activities: activitiesList,
      });
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    }
  };

  const saveLocalExecutedService = async () => {
    try {
      const executedServiceOrdersData = await API.graphql(
        graphqlOperation(executedServiceOrdersByServiceOrderID, {
          serviceOrderID: serviceOrder.id,
          limit: 1,
          filter: {
            deletedAt: { attributeExists: false },
          },
        })
      );
      const executedServiceOrderItem =
        executedServiceOrdersData.data.executedServiceOrdersByServiceOrderID
          .items[0];

      if (!executedServiceOrderItem) return;

      const executedServiceOrderData = await API.graphql(
        graphqlOperation(getExecutedServiceOrder, {
          id: executedServiceOrderItem.id,
        })
      );
      const execution = executedServiceOrderData.data.getExecutedServiceOrder;

      const data = (await localGetServiceOrder(serviceOrder.id)) || {};
      await localSaveServiceOrder(serviceOrder.id, {
        ...data,
        execution,
      });
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    }
  };

  const cleanLocalData = async () => {
    try {
      await localDeleteServiceOrder(serviceOrder.id);
      setLocalData(null);
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    }
  };

  const fetchData = async (id) => {
    const data = await localGetServiceOrder(id);
    if (data) {
      setLocalData({ execution: data.execution });
      setServiceOrder(data.serviceOrder);
      setRoutines(data.execution.executedRoutines || []);
    } else if (online) fetchServiceOrder(serviceOrderID);
  };

  const onChangeObservations = (e) => {
    const { value } = e.target;
    setServiceOrder((prev) => ({
      ...prev,
      observations: value,
    }));
  };

  const onChangeActivity = (
    routineID,
    activityID,
    activityValue,
    equipmentID
  ) => {
    setRoutines((prev) => {
      const listUpdated = [...prev];
      const index = listUpdated.findIndex((it) =>
        equipmentID
          ? it.routineID === routineID && equipmentID === it.equipmentID
          : it.routineID === routineID && !it.equipmentID
      );
      const isNewActivity = listUpdated[index].activities?.some(
        (act) => act.activityID === activityID
      );

      if (!isNewActivity)
        listUpdated[index] = {
          ...listUpdated[index],
          activities: [
            ...listUpdated[index].activities,
            { activityID, activityValue },
          ],
        };
      else
        listUpdated[index] = {
          ...listUpdated[index],
          activities:
            listUpdated[index].activities?.map((act) => {
              if (act.activityID === activityID)
                return {
                  ...act,
                  activityValue,
                };

              return act;
            }) ?? [],
        };

      return listUpdated;
    });
  };

  async function fetchServiceOrder(id) {
    try {
      const serviceOrderData = await API.graphql(
        graphqlOperation(getServiceOrder, { id })
      );
      const serviceOrderItem = serviceOrderData.data.getServiceOrder;
      if (!serviceOrderItem) {
        toast.error(strings.responses.errorFetch);
        navigate("/service-orders");
        return;
      }

      if (serviceOrderItem.equipments?.items?.length > 0) {
        const equipmentsData = (
          await API.graphql(
            graphqlOperation(serviceOrderEquipmentsByServiceOrderId, {
              serviceOrderId: id,
              limit: 500,
            })
          )
        ).data.serviceOrderEquipmentsByServiceOrderId.items;
        serviceOrderItem.equipments.items =
          equipmentsData?.map((it) => it.equipment.id) ?? [];
      }

      const companyName = await fetchCompanyName(serviceOrderItem.companyID);
      const branchName = await fetchBranchName(serviceOrderItem.branchID);
      const costCenterName = await fetchCostCenterName(
        serviceOrderItem.costCenterID
      );
      const userExecutorName = serviceOrderItem.userExecutor
        ? await fetchUserEmail(serviceOrderItem.userExecutor)
        : "";

      setRoutines([
        ...(serviceOrderItem.equipmentRoutines?.map((t) => ({
          equipmentID: t.equipmentID,
          routineID: t.routineID,
          activities: [],
        })) ?? []),
        ...(serviceOrderItem.maintenanceRoutines?.items?.map((t) => ({
          routineID: t.maintenanceRoutineId,
          activities: [],
        })) ?? []),
      ]);
      setServiceOrder({
        ...serviceOrderItem,
        companyName,
        branchName,
        userExecutorName,
        costCenterName,
        tools: serviceOrderItem.tools?.items?.map((t) => t.toolId) ?? [],
        spares: serviceOrderItem.spares?.items?.map((t) => t.id) ?? [],
        maintenanceRoutines:
          serviceOrderItem.maintenanceRoutines?.items?.map(
            (t) => t.maintenanceRoutineId
          ) ?? [],
        equipments: serviceOrderItem.equipments?.items ?? [],
        equipmentRoutines:
          serviceOrderItem.equipmentRoutines?.map((eqR) => ({
            equipmentID: eqR.equipmentID,
            routineID: eqR.routineID,
          })) ?? [],
        equipmentSpares:
          serviceOrderItem.equipmentSpares?.map((eqS) => ({
            equipmentID: eqS.equipmentID,
            spares: eqS.spares,
          })) ?? [],
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  async function fetchBranchName(id) {
    try {
      const branchData = await API.graphql(
        graphqlOperation(getBranchName, {
          id,
        })
      );
      const branchItem = branchData.data.getBranch;
      return branchItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchBranchName", err);
      return "";
    }
  }

  async function fetchCostCenterName(id) {
    try {
      const costCenterData = await API.graphql(
        graphqlOperation(getCostCenter, {
          id,
        })
      );
      const costCenterItem = costCenterData.data.getCostCenter;
      return costCenterItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCostCenterName", err);
      return "";
    }
  }

  async function fetchUserEmail(id) {
    try {
      const userData = await API.graphql(
        graphqlOperation(getUserEmail, {
          id,
        })
      );
      const userItem = userData.data.getUser;
      return userItem?.email ?? "";
    } catch (err) {
      console.log("Error fetchUserEmail", err);
      return "";
    }
  }

  useEffect(() => {
    fetchData(serviceOrderID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceOrderID]);

  if (!serviceOrder) return <Loader />;

  const value = {
    viewType,
    serviceOrder,
    fromEquipment,
    routines,
    onChangeActivity,
    onChangeObservations,
    localData,
    saveLocalData,
    cleanLocalData,
    saveLocalUsers,
    saveLocalTools,
    saveLocalSpares,
    saveLocalEquipments,
    saveLocalRoutines,
    saveLocalExecutedService,
  };

  return (
    <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
  );
};

const strings = new LocalizedStrings({
  es: {
    responses: {
      errorFetch: "Error al consultar la orden de servicio",
      unexpectedError: "Ha ocurrido un error inesperado",
    },
  },
});
